import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, CircularProgress, Alert, Chip, MenuItem, TablePagination,
  FormControlLabel, Switch,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { feeTypeApi, accountingApi, fundTypeApi } from '../../services/api';
import { FeeType, Account, FundType } from '../../types';

const freqMap: Record<string, number> = { Monthly: 1, Quarterly: 2, Annually: 3, OneTime: 4 };
const freqLabels = Object.keys(freqMap);

const FeeTypeList: React.FC = () => {
  const [feeTypes, setFeeTypes] = useState<FeeType[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [fundTypes, setFundTypes] = useState<FundType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FeeType | null>(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState('Monthly');
  const [fundTypeId, setFundTypeId] = useState('');
  const [glAccountId, setGlAccountId] = useState('');
  const [isPerSqFt, setIsPerSqFt] = useState(false);
  const [isRefundable, setIsRefundable] = useState(false);
  const [isVariableAmount, setIsVariableAmount] = useState(false);
  const [percentageRate, setPercentageRate] = useState('');
  const [percentageOfFeeTypeId, setPercentageOfFeeTypeId] = useState('');
  const [formError, setFormError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [ftRes, accRes, fundRes] = await Promise.all([
        feeTypeApi.getAll(),
        accountingApi.getChartOfAccounts(),
        fundTypeApi.getAll(),
      ]);
      setFeeTypes(Array.isArray(ftRes.data) ? ftRes.data : ftRes.data?.data || []);
      const grouped = accRes.data;
      const flat: Account[] = [];
      if (grouped && typeof grouped === 'object' && !Array.isArray(grouped)) {
        Object.values(grouped).forEach((arr: any) => { if (Array.isArray(arr)) flat.push(...arr); });
      }
      setAccounts(flat);
      setFundTypes(Array.isArray(fundRes.data) ? fundRes.data : []);
    } catch (err: any) { setError('Failed to load fee types.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAddDialog = () => {
    setEditingItem(null);
    setName(''); setDescription(''); setAmount(''); setFrequency('Monthly');
    setFundTypeId(fundTypes[0]?.id ? String(fundTypes[0].id) : '');
    setGlAccountId('');
    setIsPerSqFt(false); setIsRefundable(false); setIsVariableAmount(false);
    setPercentageRate(''); setPercentageOfFeeTypeId('');
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (ft: FeeType) => {
    setEditingItem(ft);
    setName(ft.name); setDescription(ft.description); setAmount(String(ft.amount));
    setFrequency(ft.frequency); setFundTypeId(String(ft.fundTypeId));
    setGlAccountId('');
    setIsPerSqFt(ft.isPerSqFt); setIsRefundable(ft.isRefundable); setIsVariableAmount(ft.isVariableAmount);
    setPercentageRate(ft.percentageRate ? String(ft.percentageRate) : '');
    setPercentageOfFeeTypeId(ft.percentageOfFeeTypeId ? String(ft.percentageOfFeeTypeId) : '');
    setFormError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) { setFormError('Name is required.'); return; }
    if (!isVariableAmount && (!amount || Number(amount) <= 0)) { setFormError('Valid amount is required.'); return; }

    const revenueAccounts = accounts.filter(a => a.type === 'Revenue');
    const liabilityAccounts = accounts.filter(a => a.type === 'Liability');
    const defaultGlId = isRefundable ? (liabilityAccounts[0]?.id || 4) : (revenueAccounts[0]?.id || 6);
    const selectedGlId = glAccountId ? Number(glAccountId) : defaultGlId;

    setSaving(true); setFormError('');
    try {
      const payload = {
        name: name.trim(), description: description.trim(), amount: Number(amount) || 0,
        frequency: freqMap[frequency] || 1,
        fundTypeId: Number(fundTypeId) || 1,
        glAccountId: selectedGlId,
        isPerSqFt, isRefundable, isVariableAmount,
        percentageRate: Number(percentageRate) || 0,
        percentageOfFeeTypeId: percentageOfFeeTypeId ? Number(percentageOfFeeTypeId) : null,
      };
      if (editingItem) await feeTypeApi.update(editingItem.id, payload);
      else await feeTypeApi.create(payload);
      await fetchData();
      setDialogOpen(false);
    } catch (err: any) { setFormError(err.response?.data?.message || 'Failed to save fee type.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this fee type?')) return;
    try { await feeTypeApi.delete(id); await fetchData(); }
    catch (err: any) { setError(err.response?.data?.message || 'Failed to delete.'); }
  };

  const formatAmount = (ft: FeeType) => {
    if (ft.isPerSqFt) return `RM${ft.amount.toFixed(2)}/sqft`;
    if (ft.isVariableAmount) return 'Variable';
    if (ft.percentageRate > 0) return `${ft.percentageRate}% of parent`;
    return `RM${ft.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };
  const displayed = feeTypes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Fee Types</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>Add Fee Type</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Frequency</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fund</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Flags</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>GL Account</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No fee types configured.</Typography></TableCell></TableRow>
              ) : displayed.map((ft) => (
                <TableRow key={ft.id} hover>
                  <TableCell><Typography sx={{ fontWeight: 600 }}>{ft.name}</Typography></TableCell>
                  <TableCell>{formatAmount(ft)}</TableCell>
                  <TableCell><Chip label={ft.frequency} size="small" variant="outlined" /></TableCell>
                  <TableCell>{ft.fundTypeName}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {ft.isPerSqFt && <Chip label="Per SqFt" size="small" color="info" />}
                      {ft.isRefundable && <Chip label="Refundable" size="small" color="warning" />}
                      {ft.isVariableAmount && <Chip label="Variable" size="small" color="secondary" />}
                      {ft.percentageRate > 0 && <Chip label={`${ft.percentageRate}%`} size="small" color="success" />}
                    </Box>
                  </TableCell>
                  <TableCell>{ft.glAccountName || '-'}</TableCell>
                  <TableCell>
                    <Chip label={ft.isActive ? 'Active' : 'Inactive'} color={ft.isActive ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEditDialog(ft)} color="primary"><Edit /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(ft.id)} color="error"><Delete /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={feeTypes.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </TableContainer>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Fee Type' : 'Add Fee Type'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>}
          <TextField label="Name" fullWidth margin="normal" value={name} onChange={e => setName(e.target.value)} required disabled={saving} />
          <TextField label="Description" fullWidth margin="normal" value={description} onChange={e => setDescription(e.target.value)} multiline rows={2} disabled={saving} />
          <TextField label="Amount" fullWidth margin="normal" value={amount} onChange={e => setAmount(e.target.value)} type="number" required disabled={saving} />
          <TextField label="Frequency" select fullWidth margin="normal" value={frequency} onChange={e => setFrequency(e.target.value)} disabled={saving}>
            {freqLabels.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
          </TextField>
          <TextField label="Fund Type" select fullWidth margin="normal" value={fundTypeId} onChange={e => setFundTypeId(e.target.value)} disabled={saving}>
            {fundTypes.map(ft => <MenuItem key={ft.id} value={String(ft.id)}>{ft.name}</MenuItem>)}
          </TextField>
          <TextField label="GL Account" select fullWidth margin="normal" value={glAccountId} onChange={e => setGlAccountId(e.target.value)} disabled={saving}>
            {(isRefundable ? accounts.filter(a => a.type === 'Liability') : accounts.filter(a => a.type === 'Revenue')).map(a => <MenuItem key={a.id} value={String(a.id)}>{a.accountCode} - {a.name}</MenuItem>)}
          </TextField>
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <FormControlLabel control={<Switch checked={isPerSqFt} onChange={e => setIsPerSqFt(e.target.checked)} disabled={saving} />} label="Per Sq.Ft." />
            <FormControlLabel control={<Switch checked={isRefundable} onChange={e => { setIsRefundable(e.target.checked); if (e.target.checked) setIsPerSqFt(false); }} disabled={saving} />} label="Refundable" />
            <FormControlLabel control={<Switch checked={isVariableAmount} onChange={e => { setIsVariableAmount(e.target.checked); if (e.target.checked) { setAmount(''); setIsPerSqFt(false); } }} disabled={saving} />} label="Variable Amount" />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField label="Percentage Rate (%)" type="number" size="small" sx={{ width: 160 }} value={percentageRate} onChange={e => setPercentageRate(e.target.value)} disabled={saving}
              helperText="e.g. 10 for 10% of another fee" />
            {Number(percentageRate) > 0 && (
              <TextField label="Percentage Of Fee Type" select size="small" sx={{ flex: 1 }} value={percentageOfFeeTypeId} onChange={e => setPercentageOfFeeTypeId(e.target.value)} disabled={saving}>
                {feeTypes.filter(ft => ft.id !== editingItem?.id).map(ft => <MenuItem key={ft.id} value={String(ft.id)}>{ft.name}</MenuItem>)}
              </TextField>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={18} /> : undefined}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeeTypeList;
